import React, { Component, createRef, RefObject, KeyboardEvent, MouseEvent } from 'react';
import { createRoot, Root } from 'react-dom/client';
import Assign from 'lodash.assign';

const prefix = require('react-prefixr');

interface ModalStyle {
  overlay?: React.CSSProperties;
  content?: React.CSSProperties;
}

interface EffectProps {
  begin: React.CSSProperties;
  end: React.CSSProperties;
  transition?: {
    property?: string;
    duration?: number;
    timingfunction?: string;
  };
}

interface ModalProps {
  children: React.ReactNode;
  style?: ModalStyle;
  effect: EffectProps;
  onRequestClose?: () => boolean | void;
}

interface ModalState {
  open: boolean;
}

const defaultStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99999999,
    overflow: 'hidden',
    perspective: 1300,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },

  content: {
    position: 'relative',
    margin: '15% auto',
    width: '60%',
    border: '1px solid rgba(0, 0, 0, .2)',
    background: '#fff',
    overflow: 'auto',
    borderRadius: '4px',
    outline: 'none',
    boxShadow: '0 5px 10px rgba(0, 0, 0, .3)',
  },
};

const defaultTransition = {
  property: 'all',
  duration: 300,
  timingfunction: 'linear',
};

const stopPropagation = (e: MouseEvent): void => e.stopPropagation();

let onClose: ((callback: () => void) => void) | null = null;

export class Modal extends Component<ModalProps, ModalState> {
  private contentRef: RefObject<HTMLDivElement>;
  private overlayRef: RefObject<HTMLDivElement>;
  private closeTimer: NodeJS.Timeout | null = null;
  constructor(props: ModalProps) {
    super(props);
    this.state = {
      open: false,
    };
    this.contentRef = createRef<HTMLDivElement>();
    this.overlayRef = createRef<HTMLDivElement>();
  }
  close = (): void => {
    if (!this.props.onRequestClose || this.props.onRequestClose()) {
      ModalManager.close();
    }
  };
  handleKeyDown = (event: KeyboardEvent): void => {
    if (event.keyCode == 27 /*esc*/) this.close();
  };
  componentDidMount(): void {
    const transitionTimeMS = this.getTransitionDuration();
    setTimeout(() => this.setState({ open: true }), 0);
    onClose = (callback: () => void): void => {
      this.setState({ open: false }, () => {
        this.closeTimer = setTimeout(callback, transitionTimeMS);
      });
    };
  }
  componentWillUnmount(): void {
    onClose = null;
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
  getTransitionDuration(): number {
    const { effect } = this.props;
    if (!effect.transition) {
      return defaultTransition.duration;
    }
    return effect.transition.duration || defaultTransition.duration;
  }
  render() {
    const { style, effect } = this.props;
    const { open } = this.state;

    let transition = effect.transition;
    if (!transition) {
      transition = defaultTransition;
    } else {
      transition = Assign({}, defaultTransition, transition);
    }
    let transition_style = {
      transition:
        transition.property + ' ' + (transition.duration ?? 1000) / 1000 + 's' + ' ' + transition.timingfunction,
    };

    return (
      <div
        ref={this.overlayRef}
        style={prefix(
          Assign({}, defaultStyles.overlay, style?.overlay || {}, {
            transition: 'opacity ' + (transition.duration ?? 1000) / 1000 + 's' + ' linear',
            opacity: open ? 1 : 0,
          })
        )}
        onClick={this.close}
      >
        <div
          ref={this.contentRef}
          style={prefix(
            Assign({}, defaultStyles.content, style?.content || {}, transition_style, open ? effect.end : effect.begin)
          )}
          onClick={stopPropagation}
          onKeyDown={this.handleKeyDown}
        >
          {this.props.children}
        </div>
      </div>
    );
  }
}

let containerNode: HTMLDivElement | null = null;
let root: Root | null = null;
const modals: React.ReactElement[] = [];

const renderModal = (): void => {
  if (modals.length === 0) return;

  const component = modals.shift();
  if (!component) return;

  if (!containerNode) {
    containerNode = document.createElement('div');
    document.body.appendChild(containerNode);
    root = createRoot(containerNode);
  }

  if (root) {
    root.render(component);
  }
};

export const ModalManager = {
  open(component: React.ReactElement): void {
    modals.push(component);
    if (modals.length === 1) {
      // render the modal only if there is no other showing modals
      renderModal();
    }
  },
  close(): void {
    if (onClose) {
      onClose(() => {
        if (root) {
          root.render(<></>);
          renderModal(); // render the other modals which are waiting.
        }
      });
    }
  },
};
